/**
 * The settings sections, as data.
 *
 * Pure data with no imports, because two places need it and neither should import the
 * other: `SettingsLayout` draws the section list, and the main navigation decides
 * whether to show Settings at all.
 *
 * That second use is why this file exists. The navigation used to carry its own copy of
 * the answer, and the copies drifted — it granted the Settings item to anyone holding
 * `service.view` or `workflow.view`, which no settings screen serves, and withheld it
 * from `role.view`, which one does. Every paralegal in the firm saw a Settings item
 * that opened a page with an empty section list and nothing on it.
 *
 * Deriving one from the other means adding a section cannot leave the navigation stale.
 */

export interface SettingsSection {
  to: string;
  label: string;
  description: string;
  /** `[module, action]`. Any match shows the section. */
  requires: Array<[string, string]>;
}

export const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    to: '/settings/organization',
    label: 'Firm profile',
    description: 'The firm’s own record and how it looks to clients.',
    requires: [['organization', 'view']],
  },
  {
    to: '/settings/users',
    label: 'Users',
    description: 'Who works here, at what role.',
    requires: [['user', 'view']],
  },
  {
    to: '/settings/roles',
    label: 'Roles',
    description: 'What each role can do.',
    requires: [['role', 'view']],
  },
  {
    to: '/settings/departments',
    label: 'Departments',
    description: 'Practice areas.',
    requires: [['organization', 'view']],
  },
  {
    to: '/settings/offices',
    label: 'Offices',
    description: 'Physical locations.',
    requires: [['organization', 'view']],
  },
  {
    to: '/settings/email',
    label: 'Firm email',
    description: 'The address your clients’ email comes from, and replies go to.',
    requires: [['organization', 'configure']],
  },
  {
    to: '/settings/custom-fields',
    label: 'Custom fields',
    description: 'Anything this firm records that the standard forms have no room for.',
    // `configure`, not `view`. Adding a field changes what every colleague sees on
    // every lead and client, and one of them can be made visible to clients.
    requires: [['organization', 'configure']],
  },
  {
    to: '/settings/matter-types',
    label: 'Matter types',
    description: 'What the firm sells, and when work on each is locked.',
    // `service.edit`, not `service.view`. Every role that touches a matter holds the
    // view — this section is for changing the catalogue, and only the administrator may.
    requires: [['service', 'edit']],
  },
  {
    to: '/settings/client-progress',
    label: 'Client progress',
    description: 'What clients read about each stage of their matter, in your own words.',
    // `workflow.edit`, not `view`: every role reads the workflows to work a matter, and
    // this section exists to change what clients are told — administrators only.
    requires: [['workflow', 'edit']],
  },
  {
    to: '/settings/agreement-templates',
    label: 'Agreement templates',
    description: 'Standard engagement letters, picked instead of pasted in by hand.',
    // The `template` capability — already defined in the catalogue for services,
    // workflows and forms, and reused here rather than inventing a new one.
    requires: [['template', 'view']],
  },
];

/**
 * Every capability that opens at least one settings screen. This is exactly the
 * condition for showing Settings in the main navigation: hold one of these and there is
 * something in there for you, hold none and there is not.
 */
export const SETTINGS_REQUIREMENTS: Array<[string, string]> = Array.from(
  new Map(
    SETTINGS_SECTIONS.flatMap((section) => section.requires).map(
      (pair) => [pair.join('.'), pair] as const,
    ),
  ).values(),
);
